import React from 'react';
import {observable, action} from 'mobx';
import {observer} from 'mobx-react';
import PropTypes from 'prop-types';

import inject from '../../../../hoc/injectHoc';

import FlowStepper from '../../../../common/flowStepper/FlowStepper';
import PricingTabs from '../../../../../components/common/pricingTabs/PricingTabs';
import MediaStickPricingTabs from '../../../../../components/common/pricingTabs/MediaStickPricingTabs';
import OnboardUserSigns from '../onboardUserSigns/OnboardUserSigns';
import OrderDetails from '../orderDetails/OrderDetails';
import WaveGraphic from '../../../../../components/common/waveGraphic/WaveGraphic';

import './onboardingExperiences.scss';

const PLANS_SECTION_KEY = 1;
const ADDONS_SECTION_KEY = 2;
const CHECKOUT_SECTION_KEY = 3;
const ONBOARDING_FLOW_SECTIONS = [
  {
    key: PLANS_SECTION_KEY,
    label: 'Plans',
  },
  {
    key: ADDONS_SECTION_KEY,
    label: 'Add-ons',
  },
  {
    key: CHECKOUT_SECTION_KEY,
    label: 'Sign Information'
  },
];

/**
 * The StandardOnboardingExperience component.
 */
export class StandardOnboardingExperience extends React.Component {
  @observable currentSection = 1;

  /**
   * Promote the plan subscriptions
   *
   * @type {boolean}
   */
  @observable promoteContentPlans = false

  /**
   * Promote the media stick plan
   *
   * @type {boolean}
   */
  @observable promoteMediaStickPlan = false

  /**
   * Triggers on Component mount
   */
  @action componentDidMount() {
    if (this.props.mediaStickPlan) {
      this.promoteContentPlans = true;
      this.changeSection(ADDONS_SECTION_KEY, true);
    }

    if (this.props.alaCarteCredits) {
      this.changeSection(CHECKOUT_SECTION_KEY, true);
    }
  }

  /**
   * Changes the currently displayed section
   *
   * @param {string} section
   * @param {boolean} canProgress
   */
  @action changeSection = (section, canProgress) => {
    const {onPlanSelect} = this.props;
    if (section === PLANS_SECTION_KEY) {
      this.promoteContentPlans = false;
      this.promoteMediaStickPlan = false;
      onPlanSelect(null);
    }

    if ((this.currentSection === CHECKOUT_SECTION_KEY && section === ADDONS_SECTION_KEY)
      && (!this.promoteContentPlans && !this.promoteMediaStickPlan)) {
      // If the user doesn't have any addons available, take them back to the first section.
      this.currentSection = 1;
      return;
    }

    const maxSectionCount = ONBOARDING_FLOW_SECTIONS.length + 1;
    if ((section < maxSectionCount) && canProgress) {
      this.currentSection = section;
    }

    if (!canProgress && section < this.currentSection) {
      this.currentSection = section;
    }
  }

  /**
   * Purchase an a la carte credit
   *
   * @param {{
   *  download: number,
   *  request: number
   * }} alaCarteCreditsParam
   */
  @action onPurchaseCredits = (alaCarteCreditsParam) => {
    const {setAlaCarteCredits} = this.props;
    setAlaCarteCredits(alaCarteCreditsParam);

    this.changeSection(CHECKOUT_SECTION_KEY, true);
  }

    /**
     * Purchase media sticks
     *
     * @param {'monthly' | 'lifetime'} frequency
     * @param {number} quantity
     * @returns {{}}
     */
    @action onPurchaseMediaSticks = (frequency, quantity) => {
      const {setMediaStickPlan} = this.props;

      setMediaStickPlan(frequency, quantity);

      if (this.currentSection === PLANS_SECTION_KEY) {
        this.promoteContentPlans = true;
      }

      return this.changeSection(this.currentSection + 1, true);
    }

  /**
   * When paid plan is selected.
   *
   * @param {{}} selectedPlan
   */
  @action onPlanClick = (selectedPlan) => {
    const {onPlanSelect, currentPlan} = this.props;
    if (!currentPlan || selectedPlan.id !== currentPlan.id) {
      onPlanSelect(selectedPlan);
      if (this.currentSection === PLANS_SECTION_KEY) {
        this.promoteMediaStickPlan = true;
      }
      this.changeSection(this.currentSection + 1, true);
    }
  }

  /**
   * Renders the current section of the onboarding process
   * @param {number} currentSection
   *
   * @returns {{}}
   */
  renderSection() {
    const {
      billingType,
      currentPlan,
      user,
      userSigns,
      checkoutWithChargebee,
      isLoadingChargebeeCheckout,
      onChangeBillingType,
      alaCarteCredits,
      startingPlanId,
      mediaStickPlan
    } = this.props;

    const contentPlans = (<PricingTabs
      selectPlanId={currentPlan && currentPlan.id || startingPlanId}
      onPlanSelect={this.onPlanClick}
      onPurchaseCredits={this.onPurchaseCredits}
      user={user}
      tabSelected={this.startingAlaCarteCredit ? 'credits' : 'subscription'}
    />);

    const mediaStickPlans = (<MediaStickPricingTabs
      onPurchase={this.onPurchaseMediaSticks}
      user={user}
    />);
    const SECTION_DICT = {
      [PLANS_SECTION_KEY]: (<>
        {mediaStickPlans}
        {contentPlans}
      </>),
      [ADDONS_SECTION_KEY]: (
        <div>
          <h5 className="text-uppercase font-weight-bold">
            OPTIONAL ADD-ONS
          </h5>
          <div className="d-flex justify-content-between mt-3">
            {this.promoteContentPlans && (<div>
              Make your Project Content media player shine with access to our library of over 1000 professionally designed templates.<br />
              Questions? Email our team to schedule a demo: <a href="mailto:info@projectcontent.com">info@projectcontent.com</a>
            </div>)}
            {this.promoteMediaStickPlan && (<div>
              Supercharge your indoor screens with a Project Content media player, turning any indoor screen into a sign.<br />
              Questions? Email our team to schedule a demo: <a href="mailto:info@projectcontent.com">info@projectcontent.com</a>
            </div>)}
            <button
              type="button"
              className="btn btn-primary text-light"
              onClick={() => this.changeSection(this.currentSection + 1, true)}
            >
              Skip →
            </button>
          </div>
          <div className="mt-3">
            {this.promoteMediaStickPlan && mediaStickPlans}
            {this.promoteContentPlans && contentPlans}
          </div>
        </div>
      ),
      [CHECKOUT_SECTION_KEY]: <div className="row">
        <OnboardUserSigns
          initializeDefaultSignValues={mediaStickPlan ? 'mediaStickSign' : null}
        />
        {(currentPlan || mediaStickPlan || alaCarteCredits) && (<OrderDetails
          billingType={billingType}
          buttonText="Confirm Purchase"
          isLoading={isLoadingChargebeeCheckout}
          plan={currentPlan}
          alaCarteCredits={alaCarteCredits}
          mediaStickPlan={mediaStickPlan}
          onChangeBillingType={onChangeBillingType}
          userSigns={userSigns}
          onSubmit={checkoutWithChargebee}
        />)}
      </div>
    };
    return (
      <div className="container px-0">
        <div className="onboarding-content">
          {SECTION_DICT[this.currentSection]}
          <WaveGraphic
            waveGraphicClass="onboarding"
          />
        </div>
      </div>
    );
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    return (
      <>
        <FlowStepper
          steps={ONBOARDING_FLOW_SECTIONS}
          currentStep={this.currentSection}
          changeStep={this.changeSection}
        />
          {this.renderSection()}
      </>
    );
  }
}

StandardOnboardingExperience.propTypes = {
  billingType: PropTypes.string.isRequired,
  checkoutWithChargebee: PropTypes.func.isRequired,
  isLoadingChargebeeCheckout: PropTypes.bool.isRequired,
  onChangeBillingType: PropTypes.func.isRequired,
  onPlanSelect: PropTypes.func.isRequired,
  setAlaCarteCredits: PropTypes.func.isRequired,
  setMediaStickPlan: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,

  alaCarteCredits: PropTypes.object,
  currentPlan: PropTypes.object,
  mediaStickPlan: PropTypes.object,
  startingPlanId: PropTypes.number,
  userSigns: PropTypes.object,
};

StandardOnboardingExperience.wrappedComponent = {};
StandardOnboardingExperience.wrappedComponent.propTypes = {
};

export default inject(StandardOnboardingExperience)(
  observer(StandardOnboardingExperience)
);
